import { Link, useLoaderData } from '@remix-run/react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { ProductCarousel } from '#app/components/product-carousel'
import ButtonAndLinks from '#app/components/typography/button-and-links'
import { Button } from '#app/components/ui/button'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'
import { type loader as PDPLoader } from '#app/routes/_public+/product+/$styleNumber'

const ClerkCarouselSuggestion = () => {
	let { t } = useTranslation('product_details_page')
	const { trackAmplitudeEvent } = useAmplitudeContext()
	const similarLinkParams = []
	const brandLinkParams = []
	const clerkLoaderData = useLoaderData<typeof PDPLoader>()

	if (
		!clerkLoaderData ||
		!clerkLoaderData.productData ||
		!clerkLoaderData.similarClerkProductData ||
		!clerkLoaderData.specificBrandClerkProducts
	) {
		return null
	}

	const similarClerkData = clerkLoaderData.similarClerkProductData
	const specificBrandClerkProductsData = clerkLoaderData.specificBrandClerkProducts

	const activeStyle =
		clerkLoaderData.productData.styleAvailableVariants.find(
			(it: { active: any }) => it.active,
		) || clerkLoaderData.productData.styleAvailableVariants[0]
	const category =
		clerkLoaderData.productData.productInfo?.categories[0]?.obj?.key
	const bodyType = activeStyle?.variants[0].attributes?.find(
		(item: { name: string }) => item.name === 'body_type',
	)?.value
	const brand = clerkLoaderData.productData.brand?.value

	if (brand) {
		const brandParamsString = `brand=${encodeURIComponent(brand)}&`
		brandLinkParams.push(brandParamsString)
	}

	if (bodyType?.en?.length) {
		const bodyParamsString = `bodytype=${encodeURIComponent(bodyType.en)}&`
		if (bodyParamsString) {
			similarLinkParams.push(bodyParamsString)
			brandLinkParams.push(bodyParamsString)
		}
	}
	if (clerkLoaderData?.storeParams) {
		const storeParamsString = clerkLoaderData?.storeParams
			.map((store: string | number | boolean) => `${encodeURIComponent(store)}`)
			.join(',')
		if (storeParamsString) {
			similarLinkParams.push(`store=${storeParamsString}`)
			brandLinkParams.push(`store=${storeParamsString}`)
		}
	}

	const paramsForBrandedUrl =
		brandLinkParams.length > 0 ? `?${brandLinkParams.join('&')}` : ''

	const paramsForSimilarUrl =
		similarLinkParams.length > 0 ? `?${similarLinkParams.join('&')}` : ''

	const showMostPopular = clerkLoaderData.storeParams?.length > 0
	const title = showMostPopular
		? `${t('new_products')} ${brand}`
		: `${t('more_from')} ${brand}` // More from brand is the fallback when not showing most popular

	const hasSimilarData =
		similarClerkData && similarClerkData.product_data?.length > 0
	
	const hasBrandData =
		specificBrandClerkProductsData &&
		specificBrandClerkProductsData.product_data?.length > 0

	return (
		<>
			{hasSimilarData && (
				<div className="flex flex-col space-y-8">
					<div className="px-4 md:pl-[var(--plenty-padding)] md:pr-0">
						<ProductCarousel
							clerkProducts={similarClerkData.product_data}
							title={t('similar_items')}
							subtitle={t('similar_items_subheader')}
							storeParams={clerkLoaderData?.storeParams}
						/>
					</div>
					<div className="flex justify-center px-4">
						<Link
							to={`/products/${category}${paramsForSimilarUrl}`}
							onClickCapture={() =>
								trackAmplitudeEvent('similar carousel view all')
							}
						>
							<Button variant="primary" size="primary">
								<ButtonAndLinks as="s" body={t('view_all_similar_items')} />
							</Button>
						</Link>
					</div>
				</div>
			)}

			

			{hasBrandData && (
				<div
					style={{
						backgroundRepeat: 'no-repeat',
						backgroundPosition: 'left top',
					}}
					className="bg-pill-mobile flex flex-col space-y-8"
				>
					<div className="mt-10 px-4 md:pl-[var(--plenty-padding)] md:pr-0">
						<ProductCarousel
							clerkProducts={specificBrandClerkProductsData.product_data}
							title={title}
							subtitle={t('more_from_brand_subheader')}
							storeParams={clerkLoaderData?.storeParams}
						/>
					</div>
					<div className="flex justify-center px-4">
						<Link
							to={`/products/${category}${paramsForBrandedUrl}`}
							onClickCapture={() =>
								trackAmplitudeEvent('brand carousel view all')
							}
						>
							<Button variant="primary" size="primary">
								<ButtonAndLinks as="s" body={t('view_more_from_brand')} />
							</Button>
						</Link>
					</div>
				</div>
			)}
		</>
	)
}

export default ClerkCarouselSuggestion
